import {
  useState,
  MouseEvent,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  faAngleDown,
  faAngleUp,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightFromBracket, faSliders } from '@fortawesome/pro-light-svg-icons';

import { ColorTheme } from 'enum/ColorTheme';
import { AdminAccess, UserRole } from 'enum/UserRole';
import { supportedLanguages } from 'constants/languages';
import { USER_PROFILE_URL } from 'constants/serviceUrls';
import { RoutePath } from 'constants/RoutePath';
import {
  userSelector,
  accessSelector,
  avatarSelector,
  serviceSelector,
  servicesSelector,
} from 'store/auth/authSelectors';
import { logoutAccount } from 'store/auth/authThunk';
import { setLanguage, setTimeZone } from 'store/app/appReducer';
import { languageSelector, timeZoneSelector } from 'store/app/appSelectors';
import AppTimeZoneSelect from 'components/shared/AppTimeZoneSelect/AppTimeZoneSelect';
import AppSelect from 'components/shared/AppSelect/AppSelect';
import { useTypedDispatch } from 'hooks/useTypedDispatch';
import { useHistoryStack } from 'hooks/useHistoryStack';

import ListMenuItem from './components/ListMenuItem';

import './AccountMenu.scss';

const AccountMenu = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useTypedDispatch();
  const history = useHistoryStack();

  const user = useSelector(userSelector);
  const isSuperAdmin = useMemo(() => user?.role === UserRole.SUPER_USER, [user?.role]);
  const services = useSelector(servicesSelector);
  const access = useSelector(accessSelector);
  const serviceData = useSelector(serviceSelector);
  const avatar = useSelector(avatarSelector);
  const timeZone = useSelector(timeZoneSelector);
  const language = useSelector(languageSelector);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const userData = useMemo(() => {
    if (user) {
      const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
      return {
        name,
        email: user.email,
        placeholder: name?.[0] || user?.email?.[0],
      };
    }
    return {};
  }, [user]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleIconClick = (event: MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    handleClose();
  };

  const handleLogoutClick = () => {
    dispatch(logoutAccount());
    history.clear();
    handleClose();
  };

  const handlePreferencesClick = () => {
    handleClose();
  };

  const menu = useMemo(() => [
    {
      label: t('preferences'),
      onClick: handlePreferencesClick,
      icon: <FontAwesomeIcon icon={faSliders} />,
      hidden: true,
    },
    {
      label: t('logout'),
      onClick: handleLogoutClick,
      icon: <FontAwesomeIcon icon={faArrowRightFromBracket} />,
    },
  ], [i18n.language]);

  const userRole = useMemo(() => {
    if (!serviceData) return '';
    if (access === AdminAccess.ADMIN_LITE) return t('maintainer');
    if (access === AdminAccess.ADMIN) return t('admin');
    return t('member');
  }, [access, serviceData, i18n.language]);

  const servicesList = useMemo(() => (
    services?.map(({
      id,
      shortName,
      name,
      logo,
      access,
      accountName,
      accountShortName,
    }) => (
      <ListMenuItem
        key={id}
        shortName={shortName}
        name={name}
        logo={logo}
        access={access}
        accountName={accountName}
        accountShortName={accountShortName}
        className={id === serviceData?.id ? 'selected-item' : undefined}
        onClick={handleClose}
      />
    )) || []
  ), [services, serviceData]);

  return (
    <Box className="account-menu">
      <Box>
        <IconButton
          onClick={handleClick}
          size="small"
          className="account-menu__logo"
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Avatar src={avatar || ''}>
            {userData?.placeholder}
          </Avatar>
          <Box className="name-wrapper" data-testid="user-role-testid">
            {userData?.name || userData?.email}
            {userRole && <span className="role">{userRole}</span>}
          </Box>
          <FontAwesomeIcon icon={open ? faAngleUp : faAngleDown} />
        </IconButton>
      </Box>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        className="account-menu__menu"
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <Box
          className="account-menu__menu-header"
          component={Link}
          to={USER_PROFILE_URL}
          onClick={handleClose}
          target="_blank"
        >
          <Avatar src={avatar || ''}>
            {userData?.placeholder}
          </Avatar>
          <Box className="account-menu__menu-header-info">
            <Typography className="name">{userData?.name}</Typography>
            <Typography className="email">{userData?.email}</Typography>
          </Box>
          <FontAwesomeIcon
            icon={faXmark}
            className="close-icon"
            onClick={handleIconClick}
          />
        </Box>
        <Box className="account-menu__menu-timezone">
          <Box>
            <Typography className="title">{t('timezone')}</Typography>
            <AppTimeZoneSelect
              disableClearable
              selectedOption={timeZone}
              onChange={(value) => dispatch(setTimeZone(value))}
              className="account-menu__menu-timezone-select"
              theme={ColorTheme.RED}
            />
          </Box>
          <AppSelect
            options={supportedLanguages}
            initialValue={language}
            formatValue={(value) => value.toUpperCase()}
            onChangeValue={(value) => dispatch(setLanguage(value))}
            className="language-select"
            theme={ColorTheme.RED}
          />
        </Box>
        <Box
          className="account-menu__menu-services"
        >
          {servicesList?.length > 0 && (
            <>
              <Typography className="title">{t('services')}</Typography>
              <Box className="account-menu__menu-services-wrapper">
                {servicesList}
              </Box>
            </>
          )}
          <Box
            className="account-menu__menu-services-manage"
            component={Link}
            to={RoutePath.HOME}
            onClick={handleClose}
          >
            {t('manageServices')}
          </Box>
        </Box>
        {isSuperAdmin && (
          <Box className="account-menu__menu-store">
            <Box
              className="account-menu__menu-store-link"
              component={Link}
              to={RoutePath.PLUG_INS_STORE}
              onClick={handleClose}
            >
              {t('generalStore')}
            </Box>
          </Box>
        )}
        <Box className="account-menu__menu-links">
          {menu
            .filter(({ hidden }) => !hidden)
            .map((item) => (
              <MenuItem
                key={item.label}
                onClick={item.onClick}
                className="account-menu__menu-links-item"
              >
                {item.icon}
                <Typography className="link">{item.label}</Typography>
              </MenuItem>
            ))}
        </Box>
      </Menu>
    </Box>
  );
};

export default AccountMenu;
